import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  DesignCard,
  KaraokeCard,
  MediaCard,
  SidebarOption,
} from '@src/types.ts/general';
import { findOriginalVideoElementAtCurrentTime } from '@src/utility/elements';
import { useMemo } from 'react';

import type {
  FloatingToolbarHost,
  ToolbarSettingsPanel,
} from '@src/components/common/floatingToolbar/FloatingToolbarHost';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

/**
 * Where each bar's More goes in Creator Studio: the panel selection routes
 * that element to (PLAT-12098 DE5). The Media panel picks Photo / Video
 * settings from the active element itself; Graphics › Text and Captions ›
 * Styles likewise.
 */
const PANELS: Record<
  ToolbarSettingsPanel,
  (store: VideoCreatorStore) => { isOn: boolean; open: () => void }
> = {
  photo: store => ({
    isOn:
      store.sidebarOptions === SidebarOption.media &&
      store.mediaSubMenu === MediaCard.photo,
    open: () => {
      store.sidebarOptions = SidebarOption.media;
      store.mediaSubMenu = MediaCard.photo;
    },
  }),
  video: store => ({
    isOn:
      store.sidebarOptions === SidebarOption.media &&
      store.mediaSubMenu === MediaCard.video,
    open: () => {
      store.sidebarOptions = SidebarOption.media;
      store.mediaSubMenu = MediaCard.video;
    },
  }),
  text: store => ({
    isOn:
      store.sidebarOptions === SidebarOption.design &&
      store.designSubMenu === DesignCard.text,
    open: () => {
      store.sidebarOptions = SidebarOption.design;
      store.designSubMenu = DesignCard.text;
    },
  }),
  captions: store => ({
    isOn:
      store.sidebarOptions === SidebarOption.karaoke &&
      store.karaokeSubMenu === KaraokeCard.styles,
    open: () => {
      store.sidebarOptions = SidebarOption.karaoke;
      store.karaokeSubMenu = KaraokeCard.styles;
    },
  }),
};

/**
 * PLAT-12101 DS2 — the floating toolbars' host on the Creator Studio stage:
 * getters over `VideoCreatorStore`, so the observer bars track the same MobX
 * state they read directly before, plus the sidebar for More. The only place
 * the bars' Creator Studio wiring lives.
 */
export function createCreatorStudioToolbarHost(
  store: VideoCreatorStore,
): FloatingToolbarHost {
  return {
    get activeElementIds() {
      return store.activeElementIds;
    },
    getActiveElement: () => store.getActiveElement(),
    selectElement: id => {
      // Push the pending source first, so the renderer knows a just-made id.
      void store.rootStore.videoSourceStore
        .flushChanges()
        .then(() => store.setActiveElements(id));
    },
    get isPlaying() {
      return store.isPlaying;
    },
    get canvasElement() {
      return store.renderer?.element;
    },
    get topLevelElements() {
      return store.rootStore.videoSourceStore.source.elements;
    },
    isOriginalVideoElement: source => store.isOriginalVideoElement(source),
    get editorStore() {
      return store.rootStore.editorStore;
    },
    persist: () => {
      // asFinal=false, withRenderer=true, resetTimeline=false, autoSave=true —
      // the graphics-card chip's save (PLAT-10191).
      void store.saveStoryAndVideo(false, true, false, true);
    },
    get textProducer() {
      return store.textProducer;
    },
    layout: {
      get videoSourceStore() {
        return store.rootStore.videoSourceStore;
      },
      get sceneLayoutIndexStore() {
        return store.rootStore.sceneLayoutIndexStore;
      },
      get sceneLayoutSelectionManager() {
        return store.sceneLayoutSelectionManager;
      },
      get currentVideo() {
        return store.currentVideo;
      },
    },
    captions: {
      get textBrandingService() {
        return store.textBrandingService;
      },
      get karaokeProducer() {
        return store.karaokeProducer;
      },
      get time() {
        return store.time;
      },
      get aspectRatio() {
        return store.getCurrentAspectRatio();
      },
      get customFonts() {
        return store.organization?.branding?.customFonts || [];
      },
      get sceneLayoutIndexStore() {
        return store.rootStore.sceneLayoutIndexStore;
      },
      get originalVideoElementIdAtTime() {
        return findOriginalVideoElementAtCurrentTime(store)?.id ?? undefined;
      },
      toggle: store,
      offerStyle: true,
    },
    settings: {
      isOpen: panel =>
        store.rootStore.layoutStore.isSidebarExpanded &&
        PANELS[panel](store).isOn,
      toggle: panel => {
        const target = PANELS[panel](store);
        if (store.rootStore.layoutStore.isSidebarExpanded && target.isOn) {
          store.collapseSidebar();
          return;
        }
        target.open();
        store.rootStore.layoutStore.setIsSidebarExpanded(true);
      },
    },
  };
}

/** The Creator Studio host, built once per store. */
export function useCreatorStudioToolbarHost(): FloatingToolbarHost {
  const store = useVideoCreatorStore();
  return useMemo(
    () => createCreatorStudioToolbarHost(store as VideoCreatorStore),
    [store],
  );
}
